<template>
  <div class="notification-bell" ref="bellRef">
    <button 
      class="bell-trigger"
      aria-label="通知中心"
      :aria-expanded="isOpen"
      @click="togglePanel"
      :class="{ 'has-unread': unreadCount > 0 }"
    >
      <i class="fas fa-bell"></i>
      <span v-if="unreadCount > 0" class="unread-badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>
    
    <Transition name="panel">
      <div v-if="isOpen" class="notification-panel">
        <div class="panel-header">
          <h3 class="panel-title">通知</h3>
          <div class="panel-header-actions">
            <button
              v-if="unreadCount > 0"
              class="mark-all-read"
              @click="markAllAsRead"
            >
              全部已讀
            </button>
          </div>
        </div>
        
        <div class="panel-body">
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>載入中...</span>
          </div>
          
          <div v-else-if="notifications.length === 0" class="empty-state">
            <i class="fas fa-bell-slash"></i>
            <span>暫無通知</span>
          </div>
          
          <div v-else class="notification-list">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="notification-item"
              :class="{ 'unread': !notification.isRead }"
              @click="handleNotificationClick(notification)"
            >
              <div class="notification-icon" :class="getIconClass(notification.type)">
                <i :class="getIcon(notification.type)"></i>
              </div>
              <div class="notification-content">
                <div class="notification-title">{{ notification.title }}</div>
                <div class="notification-message">{{ notification.content }}</div>
                <div class="notification-time">{{ formatTime(notification.createdAt) }}</div>
              </div>
              <div v-if="!notification.isRead" class="unread-dot"></div>
            </div>
          </div>
        </div>
        
        <div v-if="notifications.length > 0" class="panel-footer">
          <button class="view-all-btn" @click="viewAllNotifications">
            查看全部通知
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, writeBatch, Timestamp } from 'firebase/firestore'
import type { Unsubscribe } from 'firebase/firestore'
import { db } from '@/api/firebase'
import { useUserStore } from '@/store/user'
import { useRouter } from 'vue-router'

interface Notification {
  id: string
  type: string
  title: string
  content: string
  relatedType: string | null
  relatedId: string | null
  isRead: boolean
  readAt: any
  readBy: string | null
  targetRole: string | null
  targetUserId: string | null
  createdAt: any
}

const userStore = useUserStore()
const router = useRouter()

const isOpen = ref(false)
const loading = ref(false)
const notifications = ref<Notification[]>([])
const bellRef = ref<HTMLElement | null>(null)
const unsubscribeNotifications = ref<Unsubscribe | null>(null)

const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.isRead).length
})

const togglePanel = () => {
  isOpen.value = !isOpen.value
}

const subscribeNotifications = () => {
  if (!userStore.currentUser?.uid) return

  const uid = userStore.currentUser.uid

  // Unsubscribe from previous listener if any
  if (unsubscribeNotifications.value) {
    unsubscribeNotifications.value()
  }

  let notificationsQuery

  if (userStore.isFullAdmin) {
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('targetRole', '==', 'admin'),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
  } else if (userStore.isSales) {
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('targetRole', '==', 'sales'),
      where('targetUserId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
  } else if (userStore.isOP) {
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('targetRole', '==', 'op'),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
  } else if (userStore.isAccounting) {
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('targetRole', '==', 'accounting'),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
  } else if (userStore.isAffiliate) {
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('targetRole', '==', 'affiliate'),
      where('targetUserId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    )
  } else {
    // No matching role, don't subscribe
    return
  }

  loading.value = true
  unsubscribeNotifications.value = onSnapshot(notificationsQuery, (snapshot) => {
    notifications.value = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    })) as Notification[]
    loading.value = false
  }, (error) => {
    console.error('Error subscribing to notifications:', error)
    loading.value = false
  })
}

const markAllAsRead = async () => {
  if (!userStore.currentUser?.uid) return
  
  const unreadNotifications = notifications.value.filter(n => !n.isRead)
  if (unreadNotifications.length === 0) return
  
  try {
    const batch = writeBatch(db)
    
    unreadNotifications.forEach(notification => {
      const notificationRef = doc(db, 'notifications', notification.id)
      batch.update(notificationRef, {
        isRead: true,
        readAt: Timestamp.now(),
        readBy: userStore.currentUser?.uid
      })
    })
    
    await batch.commit()
    
    // Update local state
    notifications.value = notifications.value.map(n => ({
      ...n,
      isRead: true
    }))
  } catch (error) {
    console.error('Error marking all as read:', error)
  }
}

const markAsRead = async (notification: Notification) => {
  if (notification.isRead || !userStore.currentUser?.uid) return
  
  try {
    await updateDoc(doc(db, 'notifications', notification.id), {
      isRead: true,
      readAt: Timestamp.now(),
      readBy: userStore.currentUser.uid
    })
    
    // Update local state
    const index = notifications.value.findIndex(n => n.id === notification.id)
    if (index !== -1) {
      notifications.value[index].isRead = true
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

const handleNotificationClick = async (notification: Notification) => {
  await markAsRead(notification)
  
  // Navigate based on notification type
  if (notification.relatedType && notification.relatedId) {
    switch (notification.relatedType) {
      case 'order':
        router.push('/admin/orders')
        break
      case 'application':
        router.push({ path: '/admin/applications', query: { application: notification.relatedId } })
        break
      case 'affiliate':
        router.push('/admin/affiliates')
        break
      case 'settlement':
        router.push('/admin/payouts')
        break
    }
  }
  
  isOpen.value = false
}

const viewAllNotifications = () => {
  isOpen.value = false
}

const getIcon = (type: string) => {
  const icons: Record<string, string> = {
    new_order: 'fas fa-shopping-cart',
    new_application: 'fas fa-user-plus',
    order_status: 'fas fa-exchange-alt',
    settlement: 'fas fa-money-bill-wave',
    system: 'fas fa-cog'
  }
  return icons[type] || 'fas fa-bell'
}

const getIconClass = (type: string) => {
  const classes: Record<string, string> = {
    new_order: 'icon--primary',
    new_application: 'icon--success',
    order_status: 'icon--info',
    settlement: 'icon--warning',
    system: 'icon--gray'
  }
  return classes[type] || 'icon--gray'
}

const formatTime = (timestamp: any) => {
  if (!timestamp) return ''
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '剛剛'
  if (minutes < 60) return `${minutes} 分鐘前`
  if (hours < 24) return `${hours} 小時前`
  if (days < 7) return `${days} 天前`
  
  return date.toLocaleDateString('zh-TW', {
    month: 'short',
    day: 'numeric'
  })
}

const handleClickOutside = (event: MouseEvent) => {
  if (bellRef.value && !bellRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  subscribeNotifications()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (unsubscribeNotifications.value) {
    unsubscribeNotifications.value()
  }
})
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.bell-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  border-radius: var(--radius-full);
  color: var(--c-gray-600);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.bell-trigger:hover {
  background-color: var(--c-gray-100);
  color: var(--c-primary);
}

.bell-trigger.has-unread {
  color: var(--c-primary);
}

.bell-trigger i {
  font-size: 18px;
}

.unread-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background-color: var(--c-danger);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-height: 480px;
  background-color: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp-4);
  border-bottom: 1px solid var(--c-border);
  background-color: var(--c-gray-50);
}

.panel-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--c-gray-800);
}

.panel-header-actions {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}

.mark-all-read,
.clear-all-btn {
  padding: var(--sp-1) var(--sp-2);
  background: none;
  border: none;
  font-size: var(--font-size-xs);
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

.mark-all-read {
  color: var(--c-primary);
}

.clear-all-btn {
  color: var(--c-gray-500);
}

.mark-all-read:hover,
.clear-all-btn:hover {
  opacity: 0.8;
}

.delete-notification-btn {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: var(--radius-full);
  color: var(--c-gray-400);
  cursor: pointer;
  opacity: 0;
  transition: all var(--transition-fast);
  font-size: 10px;
}

.notification-item:hover .delete-notification-btn {
  opacity: 1;
}

.delete-notification-btn:hover {
  background-color: var(--c-gray-200);
  color: var(--c-gray-600);
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  max-height: 360px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--sp-8);
  color: var(--c-gray-500);
  gap: var(--sp-2);
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--c-gray-200);
  border-top-color: var(--c-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 24px;
}

.notification-list {
  display: flex;
  flex-direction: column;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  cursor: pointer;
  transition: background-color var(--transition-fast);
  border-bottom: 1px solid var(--c-border-light);
  position: relative;
}

.notification-item:hover {
  background-color: var(--c-gray-50);
}

.notification-item.unread {
  background-color: var(--c-primary-50);
}

.notification-item.unread:hover {
  background-color: var(--c-primary-100);
}

.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notification-icon.icon--primary {
  background-color: var(--c-primary-100);
  color: var(--c-primary);
}

.notification-icon.icon--success {
  background-color: var(--c-success-bg);
  color: var(--c-success);
}

.notification-icon.icon--info {
  background-color: var(--c-info-bg);
  color: var(--c-info);
}

.notification-icon.icon--warning {
  background-color: var(--c-warning-bg);
  color: var(--c-warning);
}

.notification-icon.icon--gray {
  background-color: var(--c-gray-100);
  color: var(--c-gray-600);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--c-gray-800);
  margin-bottom: var(--sp-1);
}

.notification-message {
  font-size: var(--font-size-xs);
  color: var(--c-gray-600);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: var(--font-size-xs);
  color: var(--c-gray-400);
  margin-top: var(--sp-1);
}

.unread-dot {
  width: 8px;
  height: 8px;
  background-color: var(--c-primary);
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: var(--sp-1);
}

.panel-footer {
  padding: var(--sp-3);
  border-top: 1px solid var(--c-border);
  text-align: center;
}

.view-all-btn {
  width: 100%;
  padding: var(--sp-2);
  background: none;
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  font-size: var(--font-size-sm);
  color: var(--c-gray-700);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.view-all-btn:hover {
  background-color: var(--c-gray-50);
  border-color: var(--c-primary);
  color: var(--c-primary);
}

/* Animation */
.panel-enter-active,
.panel-leave-active {
  transition: all 0.2s ease;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Responsive */
@media (max-width: 480px) {
  .notification-panel {
    position: fixed;
    top: calc(var(--navbar-height) + 8px);
    right: 8px;
    left: 8px;
    width: auto;
  }
}
</style>
